import { FilterFilled, FilterTwoTone } from '@ant-design/icons';
import { Breadcrumb, Button, Form, Layout, Modal, Progress, Row, Space, Spin, Table, Tooltip, Tree, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import RoleEnum from '~Api/Administrator/RoleEnum';
import CloseReasonEnum from '~Api/Application/CloseReasonEnum';
import ExtensionTypeEnum from '~Api/Application/ExtensionTypeEnum';
import IApplication from '~Api/Application/IApplication';
import MortgageTypeEnum from '~Api/Application/MortgageTypeEnum';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { IDictionary } from '~utilities/IDictionary';
import ITreeStructureGroup from '~utilities/ITreeStructureGroup';
import {
    applicationsSettlementForecastListAction,
    applicationsSettlementProbabilitiesListAction,
} from './actions';
import {
    applicationSettlementProbabilitiesSelector,
    settlementForecastApplicationsSelector,
} from './selectors';
import CheckedKeys from '~utilities/CheckedKeys';
import ModalStateEnum from '~UI/ModalStateEnum';
import { currencyFormatter } from '~utilities/formatters';
import { green, lime, orange, red, yellow } from '@ant-design/colors';
import { Column, ColumnConfig } from '@ant-design/plots';
import { Datum } from '@ant-design/charts';
import ISettlementProbability from '~Api/Application/ISettlementProbability';

const applicationStatusLabels: IDictionary<string> = {
    [WorkflowStatusEnum.ConditionalApproval]: 'Conditional Approval',
    [WorkflowStatusEnum.Draft]: 'Draft',
    [WorkflowStatusEnum.FormalApproval]: 'Formal Approval',
    [WorkflowStatusEnum.LegalDocuments]: 'Legal Documents',
    [WorkflowStatusEnum.New]: 'New',
    [WorkflowStatusEnum.Settlement]: 'Settlement',
    [WorkflowStatusEnum.Underwriting]: 'Underwriting',
    [WorkflowStatusEnum.Warehoused]: 'Warehoused',
};

const unassignedKey: string = 'UNASSIGNED';

enum ChannelTreeGroupTypeEnum {
    Broker = 'BROKER',
    Direct = 'DIRECT',
}

enum GraphDataTypeEnum {
    ExtensionsLikely = 'EXTENSIONS_LIKELY',
    ExtensionsSettled = 'EXTENSIONS_SETTLED',
    ExtensionsUnsettled = 'EXTENSIONS_UNSETTLED',
    NewLoansLikely = 'NEW_LOANS_LIKELY',
    NewLoansSettled = 'NEW_LOANS_SETTLED',
    NewLoansUnsettled = 'NEW_LOANS_UNSETTLED',
    TotalLikely = 'TOTAL_LIKELY',
    TotalSettled = 'TOTAL_SETTLED',
    TotalUnsettled = 'TOTAL_UNSETTLED',
}

const graphDataTypeLabels: IDictionary<string> = {
    [GraphDataTypeEnum.ExtensionsLikely]: 'Extensions - Unsettled Likely',
    [GraphDataTypeEnum.ExtensionsUnsettled]: 'Extensions - Unsettled Unlikely',
    [GraphDataTypeEnum.ExtensionsSettled]: 'Extensions - Settled',
    [GraphDataTypeEnum.NewLoansLikely]: 'New Loans - Unsettled Likely',
    [GraphDataTypeEnum.NewLoansUnsettled]: 'New Loans - Unsettled Unlikely',
    [GraphDataTypeEnum.NewLoansSettled]: 'New Loans - Settled',
    [GraphDataTypeEnum.TotalLikely]: 'Total - Unsettled Likely',
    [GraphDataTypeEnum.TotalUnsettled]: 'Total - Unsettled Unlikely',
    [GraphDataTypeEnum.TotalSettled]: 'Total - Settled',
};

const colourMaps: IDictionary<string> = {
    [GraphDataTypeEnum.ExtensionsLikely]: '#f06305a8',
    [GraphDataTypeEnum.ExtensionsUnsettled]: '#f0630554',
    [GraphDataTypeEnum.ExtensionsSettled]: '#f06305',
    [GraphDataTypeEnum.NewLoansLikely]: '#006eb8a8',
    [GraphDataTypeEnum.NewLoansUnsettled]: '#006eb854',
    [GraphDataTypeEnum.NewLoansSettled]: '#006eb8',
    [GraphDataTypeEnum.TotalLikely]: '#00c48ca8',
    [GraphDataTypeEnum.TotalUnsettled]: '#00c48c54',
    [GraphDataTypeEnum.TotalSettled]: '#00c48c',
};

interface IGraphData {
    amount: number;
    group: string;
    month: string;
    type: GraphDataTypeEnum;
}

interface IMonthData {
    extensionsLikely: number;
    extensionsSettled: number;
    extensionsUnsettled: number;
    month: string;
    newLoansLikely: number;
    newLoansSettled: number;
    newLoansUnsettled: number;
}

interface IDay {
    applicationCount: number;
    date: string;
    loanTotal: number;
    primaryRowUuid?: string;
}

interface IForecast {
    application?: IApplication;
    date: string;
    uuid: string;
}

const tooltipCurrencyFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-AU', {
    currency: 'AUD',
    minimumFractionDigits: 0,
    style: 'currency',
});

export default function SettlementForecast(): ReactElement {
    const [ bdmFilter, setBdmFilter] = useState<string[]>([]);
    const [ channelFilter, setChannelFilter] = useState<string[]>([]);
    const [ isChangeFiltersModalOpen, setIsChangeFiltersModalOpen] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ mortgageTypeFilter, setMortgageTypeFilter] = useState<string[]>([]);

    const administrators: IDictionary<IAdministrator> = useSelector(administratorsSelector);
    const applications: IDictionary<IApplication> = useSelector(settlementForecastApplicationsSelector);
    const applicationSettlementProbabilities: IDictionary<ISettlementProbability> = useSelector(applicationSettlementProbabilitiesSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!administrators) {
            dispatch(administratorsListAction());
        }
    }, [
        administrators,
        dispatch,
    ]);

    useEffect(() => {
        dispatch(applicationsSettlementForecastListAction());

        const refreshInterval: NodeJS.Timeout = setInterval(() => {
            dispatch(applicationsSettlementForecastListAction());
        }, 5 * 60 * 1000);

        return () => {
            clearInterval(refreshInterval);
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(applicationsSettlementProbabilitiesListAction());

        const refreshInterval: NodeJS.Timeout = setInterval(() => {
            dispatch(applicationsSettlementProbabilitiesListAction());
        }, 5 * 60 * 1000);

        return () => {
            clearInterval(refreshInterval);
        };
    }, [dispatch]);

    const onChangeBdmFilter: (checkedKeys: CheckedKeys) => void = useCallback((checkedKeys: CheckedKeys): void => {
        setBdmFilter(checkedKeys as string[]);
    }, []);

    const onChangeChannelFilter: (checkedKeys: CheckedKeys) => void = useCallback((checkedKeys: CheckedKeys) => {
        setChannelFilter(checkedKeys as string[]);
    }, []);

    const onChangeMortgageTypeFilter: (checkedKeys: CheckedKeys) => void = useCallback((checkedKeys: CheckedKeys) => {
        setMortgageTypeFilter(checkedKeys as string[]);
    }, []);

    const onCancelChangeFilters: () => void = useCallback(() => {
        setIsChangeFiltersModalOpen(ModalStateEnum.Closed);
    }, []);

    const onClickChangeFilters: () => void = useCallback(() => {
        setIsChangeFiltersModalOpen(ModalStateEnum.Open);
    }, []);

    const rowClassName: (forecast: IForecast) => string = useCallback((forecast: IForecast) => {
        if (['0', '6'].includes(dayjs(forecast.date).format('d'))) {
            return 'weekend';
        }

        return '';
    }, []);

    if (!administrators || !applications) {
        return (
            <Layout className='applications forecast'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to='/applications'>Applications</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Settlement Forecast</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Space className='actions'>
                            <Button disabled={true} icon={<FilterFilled/>} />
                        </Space>
                        <Typography.Title level={2}>Settlement Forecast</Typography.Title>
                        <Spin />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    const days: IDictionary<IDay> = {};
    const expiredSettlements: IApplication[] = [];
    const forecasts: IForecast[] = [];
    const months: IDictionary<IMonthData> = {};
    const visibleBdms: IDictionary<IAdministrator> = {};

    for (let i: number = 0; i < 45; i++) {
        const loopDate: Dayjs = dayjs().add(i, 'days');
        days[loopDate.format('YYYY-MM-DD')] = {
            applicationCount: 0,
            date: loopDate.format('YYYY-MM-DD'),
            loanTotal: 0,
        };
        forecasts.push({
            date: loopDate.format('YYYY-MM-DD'),
            uuid: loopDate.format('YYYY-MM-DD'),
        });
    }

    for (let i: number = -9; i <= 2; i++) {
        const loopDate: Dayjs = dayjs().add(i, 'months');
        months[loopDate.format('YYYY-MM')] = {
            extensionsLikely: 0,
            extensionsSettled: 0,
            extensionsUnsettled: 0,
            month: loopDate.format('MMMM'),
            newLoansLikely: 0,
            newLoansSettled: 0,
            newLoansUnsettled: 0,
        };
    }

    const checkDate: Dayjs = dayjs();
    _.each(applications, (application: IApplication) => {
        const { deal } = application;

        if (!application.settlementDate) {
            return;
        }

        const bdm: IAdministrator = administrators[deal.bdmUuid];

        if (deal.bdmUuid && !visibleBdms[bdm.uuid]) {
            visibleBdms[bdm.uuid] = bdm;
        }

        if (bdmFilter.length > 0) {
            let shouldShow: boolean = false;
            if (!deal.bdmUuid && bdmFilter.includes(unassignedKey)) {
                shouldShow = true;
            } else if (deal.bdmUuid && bdmFilter.includes(bdm.uuid)) {
                shouldShow = true;
            }
            if (!shouldShow) {
                return;
            }
        }

        if (channelFilter.length > 0) {
            let shouldShow: boolean = false;
            if (channelFilter.includes(ChannelTreeGroupTypeEnum.Broker) && (deal.brokerUuid || deal.isBroker)) {
                shouldShow = true;
            }
            if (channelFilter.includes(ChannelTreeGroupTypeEnum.Direct) && !deal.brokerUuid && !deal.isBroker) {
                shouldShow = true;
            }
            if (!shouldShow) {
                return;
            }
        }

        if (mortgageTypeFilter.length > 0) {
            let shouldShow: boolean = false;
            if (application.mortgageType && mortgageTypeFilter.includes(application.mortgageType)) {
                shouldShow = true;
            }
            if (!shouldShow) {
                return;
            }
        }

        const settlementDateDayjs: Dayjs = dayjs(application.settlementDate);
        const settlementMonth: string = settlementDateDayjs.format('YYYY-MM');
        const settlementDate: string = settlementDateDayjs.format('YYYY-MM-DD');

        if (application.extensionType !== ExtensionTypeEnum.GracePeriod && (!application.closeReason || application.closeReason === CloseReasonEnum.Settled)) {
            // Settled or open and not a grace period type
            if (application.workflowStatus === WorkflowStatusEnum.Warehoused) {
                // Settled
                if (months[settlementMonth]) {
                    if (application.isExtension) {
                        months[settlementMonth].extensionsSettled += application.loanAmount;
                    } else {
                        months[settlementMonth].newLoansSettled += application.loanAmount;
                    }
                }
            } else {
                // Unsettled
                if (settlementDateDayjs.isBefore(checkDate, 'day')) {
                    // Expired (before today)
                    expiredSettlements.push(application);
                }

                if (months[settlementMonth]) {
                    // Today or future
                    if (application.isExtension) {
                        months[settlementMonth].extensionsUnsettled += application.loanAmount;
                    } else {
                        months[settlementMonth].newLoansUnsettled += application.loanAmount;
                    }

                    if (days[settlementDate]) {
                        days[settlementDate].applicationCount++;
                        days[settlementDate].loanTotal += application.loanAmount;

                        if (!days[settlementDate].primaryRowUuid) {
                            days[settlementDate].primaryRowUuid = application.uuid;
                        }

                        const initialForecast: IForecast = _.find(forecasts, (forecast: IForecast) => forecast.date === settlementDate);
                        if (!initialForecast.application) {
                            initialForecast.application = application;
                        } else {
                            forecasts.push({
                                application,
                                date: settlementDate,
                                uuid: application.uuid,
                            });
                        }
                    }
                }

                if (applicationSettlementProbabilities && applicationSettlementProbabilities[application.uuid]) {
                    const likelyDateDayjs: Dayjs = dayjs(applicationSettlementProbabilities[application.uuid].likelyDate);
                    const likelySettlementMonth: string = likelyDateDayjs.format('YYYY-MM');

                    if (months[likelySettlementMonth]) {
                        if (application.isExtension) {
                            if (likelySettlementMonth !== settlementMonth) {
                                months[likelySettlementMonth].extensionsUnsettled += application.loanAmount;
                            }
                            months[likelySettlementMonth].extensionsLikely += application.loanAmount;
                        } else {
                            if (likelySettlementMonth !== settlementMonth) {
                                months[likelySettlementMonth].newLoansUnsettled += application.loanAmount;
                            }
                            months[likelySettlementMonth].newLoansLikely += application.loanAmount;
                        }
                    }
                }
            }
        }
    });

    const graphData: IGraphData[] = [];

    _.each(months, (month: IMonthData)=> {
        const extensionsUnsettled: number = month.extensionsUnsettled - month.extensionsLikely;
        const newLoansUnsettled: number = month.newLoansUnsettled - month.newLoansLikely;
        const totalLikely: number = month.extensionsLikely + month.newLoansLikely;

        const totalSettled: number = month.extensionsSettled + month.newLoansSettled;
        const totalUnsettled: number = extensionsUnsettled + newLoansUnsettled;

        graphData.push({
            amount: extensionsUnsettled,
            group: 'extension',
            month: month.month,
            type: GraphDataTypeEnum.ExtensionsUnsettled,
        });
        graphData.push({
            amount: month.extensionsLikely,
            group: 'extension',
            month: month.month,
            type: GraphDataTypeEnum.ExtensionsLikely,
        });
        graphData.push({
            amount: month.extensionsSettled,
            group: 'extension',
            month: month.month,
            type: GraphDataTypeEnum.ExtensionsSettled,
        });
        graphData.push({
            amount: newLoansUnsettled,
            group: 'new',
            month: month.month,
            type: GraphDataTypeEnum.NewLoansUnsettled,
        });
        graphData.push({
            amount: month.newLoansLikely,
            group: 'new',
            month: month.month,
            type: GraphDataTypeEnum.NewLoansLikely,
        });
        graphData.push({
            amount: month.newLoansSettled,
            group: 'new',
            month: month.month,
            type: GraphDataTypeEnum.NewLoansSettled,
        });
        graphData.push({
            amount: totalUnsettled,
            group: 'total',
            month: month.month,
            type: GraphDataTypeEnum.TotalUnsettled,
        });
        graphData.push({
            amount: totalLikely,
            group: 'total',
            month: month.month,
            type: GraphDataTypeEnum.TotalLikely,
        });
        graphData.push({
            amount: totalSettled,
            group: 'total',
            month: month.month,
            type: GraphDataTypeEnum.TotalSettled,
        });
    });

    const columnGraphConfig: ColumnConfig = {
        animation: false,
        color: (datum: Datum) => colourMaps[datum.type],
        data: graphData,
        groupField: 'group',
        height: 250,
        isGroup: true,
        isStack: true,
        legend: false,
        seriesField: 'type',
        tooltip: {
            fields: ['amount', 'type'],
            formatter: (datum: Datum) => ({
                name: graphDataTypeLabels[datum.type],
                value: tooltipCurrencyFormatter.format(datum.amount),
            }),
        },
        xAxis: {
            label: {
                style: {
                    fill: 'rgba(0, 0, 0, 0.85)',
                    fontFamily: 'Poppins',
                    fontSize: 14,
                },
            },
        },
        xField: 'month',
        yAxis: {
            label: {
                formatter: (value: string): string => tooltipCurrencyFormatter.format(Number(value)),
                style: {
                    fill: 'rgba(0, 0, 0, 0.85)',
                    fontFamily: 'Poppins',
                    fontSize: 14,
                },
            },
        },
        yField: 'amount',
    };

    const columns: ColumnsType<IForecast> = [
        {
            className: 'date',
            render: (forecast: IForecast) => {
                if (!forecast.application) {
                    return dayjs(forecast.date).format('D/M');
                }

                const day: IDay = days[forecast.date];

                return {
                    children: dayjs(day.date).format('D/M'),
                    props: {
                        rowSpan: day.primaryRowUuid === forecast.application.uuid ? day.applicationCount : 0,
                    },
                };
            },
            title: 'Date',
            width: '6%',
        },
        {
            className: 'day',
            render: (forecast: IForecast) => {
                if (!forecast.application) {
                    return dayjs(forecast.date).format('dddd');
                }

                const day: IDay = days[forecast.date];

                return {
                    children: dayjs(day.date).format('dddd'),
                    props: {
                        rowSpan: day.primaryRowUuid === forecast.application.uuid ? day.applicationCount : 0,
                    },
                };
            },
            title: 'Day',
            width: '8%',
        },
        {
            className: 'total',
            render: (forecast: IForecast) => {
                if (!forecast.application) {
                    return '-';
                }

                const day: IDay = days[forecast.date];

                return {
                    children: day.loanTotal > 0 ? currencyFormatter.format(day.loanTotal) : '-',
                    props: {
                        rowSpan: day.primaryRowUuid === forecast.application.uuid ? day.applicationCount : 0,
                    },
                };
            },
            title: 'Total',
            width: '12%',
        },
        {
            dataIndex: 'application',
            render: (application: IApplication) => application ? (application.code) : null,
            title: 'Code',
            width: '10%',
        },
        {
            dataIndex: 'application',
            render: (application: IApplication) => {
                return {
                    children: application ? (
                        <Row key={application.uuid}>
                            <Link to={`/applications/${application.uuid}`}>{application.formattedName}</Link>
                        </Row>
                    ) : null,
                };
            },
            title: 'Name',
        },
        {
            dataIndex: 'application',
            render: (application: IApplication) => application && application.loanAmount > 0 ? currencyFormatter.format(application.loanAmount) : null,
            title: 'Amount',
            width: '14%',
        },
        {
            dataIndex: 'application',
            render: (application: IApplication) => application ? applicationStatusLabels[application.workflowStatus] : null,
            title: 'Status',
            width: '14%',
        },
        {
            dataIndex: 'application',
            render: (application: IApplication) => {
                if (!application || !applicationSettlementProbabilities || !applicationSettlementProbabilities[application.uuid]) {
                    return '';
                }

                const probability: ISettlementProbability = applicationSettlementProbabilities[application.uuid];

                const progress: ReactElement = (
                    <Tooltip title={`${probability.score}%`}>
                        <Progress
                            percent={probability.score}
                            showInfo={false}
                            steps={5}
                            strokeColor={[red[6], orange[6], yellow[6], lime[6], green[6]]}
                        />
                    </Tooltip>
                );

                const likelyDate: ReactElement = (probability.likelyDate > application.settlementDate) && (
                    <Tooltip title={`Likely to settle on or after ${dayjs(probability.likelyDate).format('D/M/YYYY')}`}>
                        +{dayjs(probability.likelyDate).diff(dayjs(application.settlementDate), 'days')}
                    </Tooltip>
                );

                return (
                    <Space>
                        {progress}
                        {likelyDate}
                    </Space>
                );
            },
            title: 'Probability',
            width: '10%',
        },
    ];

    const expiredColumns: ColumnsType<IApplication> = [
        {
            dataIndex: 'settlementDate',
            render: (settlementDate: string) => dayjs(settlementDate).format('D/M'),
            title: 'Settlement Date',
            width: '10%',
        },
        {
            dataIndex: 'code',
            title: 'Code',
            width: '10%',
        },
        {
            render: (application: IApplication) => <Link to={`/applications/${application.uuid}`}>{application.formattedName}</Link>,
            title: 'Name',
        },
        {
            dataIndex: 'loanAmount',
            render: (loanAmount: number) => loanAmount > 0 ? currencyFormatter.format(loanAmount) : '-',
            title: 'Amount',
            width: '15%',
        },
        {
            dataIndex: 'workflowStatus',
            render: (workflowStatus: WorkflowStatusEnum) => applicationStatusLabels[workflowStatus],
            title: 'Status',
            width: '15%',
        },
    ];

    const hasPastDays: boolean = _.size(expiredSettlements) > 0;
    const pastSettlementBlock: JSX.Element = hasPastDays && (
        <div className='expired-settlements'>
            <h3>Expired Settlements</h3>
            <Table
                columns={expiredColumns}
                dataSource={_.orderBy(expiredSettlements, ['settlementDate'], ['asc'])}
                pagination={false}
                rowClassName='forecast-expired-settlement-row'
                rowKey='uuid'
                size='middle'
            />
        </div>
    );

    let changeFiltersModal: JSX.Element;

    if (ModalStateEnum.Off !== isChangeFiltersModalOpen) {
        const bdmTreeData: ITreeStructureGroup[] = [
            {
                children: [],
                key: unassignedKey,
                title: 'Unassigned',
                value: unassignedKey,
            },
        ];

        _.forEach(_.sortBy(visibleBdms, ['name']), (loopAdmin: IAdministrator) => {
            if ([RoleEnum.BusinessDevelopmentManager, RoleEnum.InternalBusinessDevelopmentManager, RoleEnum.SeniorBusinessDevelopmentManager].includes(loopAdmin.role)) {
                bdmTreeData.push({
                    children: [],
                    key: loopAdmin.uuid,
                    title: loopAdmin.name,
                    value: loopAdmin.uuid,
                });
            }
        });

        const channelTreeData: ITreeStructureGroup[] = [
            {
                children: [],
                key: ChannelTreeGroupTypeEnum.Broker,
                title: 'Broker',
                value: ChannelTreeGroupTypeEnum.Broker,
            },
            {
                children: [],
                key: ChannelTreeGroupTypeEnum.Direct,
                title: 'Direct',
                value: ChannelTreeGroupTypeEnum.Direct,
            },
        ];

        const mortgageTypeTreeData: ITreeStructureGroup[] = [
            {
                children: [],
                key: MortgageTypeEnum.FirstMortgage,
                title: 'First Mortgage',
                value: MortgageTypeEnum.FirstMortgage,
            },
            {
                children: [],
                key: MortgageTypeEnum.SecondMortgage,
                title: 'Second Mortgage',
                value: MortgageTypeEnum.SecondMortgage,
            },
        ];

        changeFiltersModal = (
            <Modal
                onCancel={onCancelChangeFilters}
                onOk={onCancelChangeFilters}
                open={ModalStateEnum.Open === isChangeFiltersModalOpen}
                title='Change Filters'
                wrapClassName='settlement-forecast-change-filters-modal'
            >
                <Form.Item label='Channel'>
                    <Tree
                        checkable={true}
                        treeData={channelTreeData}
                        checkedKeys={channelFilter}
                        onCheck={onChangeChannelFilter}
                        defaultExpandAll={true}
                        selectable={false}
                    />
                </Form.Item>
                <Form.Item label='BDMs'>
                    <Tree
                        checkable={true}
                        treeData={bdmTreeData}
                        checkedKeys={bdmFilter}
                        onCheck={onChangeBdmFilter}
                        defaultExpandAll={true}
                        selectable={false}
                    />
                </Form.Item>
                <Form.Item label='Mortgage Types'>
                    <Tree
                        checkable={true}
                        treeData={mortgageTypeTreeData}
                        checkedKeys={mortgageTypeFilter}
                        onCheck={onChangeMortgageTypeFilter}
                        defaultExpandAll={true}
                        selectable={false}
                    />
                </Form.Item>
            </Modal>
        );
    }

    return (
        <Layout className='applications forecast'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/applications'>Applications</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Settlement Forecast</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Space className='actions'>
                        <Button onClick={onClickChangeFilters} icon={(bdmFilter.length > 0 || channelFilter.length > 0 || mortgageTypeFilter.length > 0) ? <FilterTwoTone/> : <FilterFilled/>} />
                    </Space>
                    <Typography.Title level={2}>Settlement Forecast</Typography.Title>
                    <Column className='graph' {...columnGraphConfig} />
                    {pastSettlementBlock}
                    <div className='forecast-current'>
                        {hasPastDays && <h3>Current Forecast</h3>}
                        <Table
                            columns={columns}
                            dataSource={_.sortBy(forecasts, ['date'], ['desc'])}
                            pagination={false}
                            rowClassName={rowClassName}
                            rowKey='uuid'
                            size='middle'
                        />
                    </div>
                </Layout.Content>
            </Layout>
            {changeFiltersModal}
        </Layout>
    );
}
